import { useEffect, useMemo, useState, type ReactElement } from 'react';
import {
    type CheckProposedPasswordRejectedReason,
    type CheckProposedPasswordResponse,
} from '@pillar-portal/api-types';
import {
    evaluatePasswordPolicy,
    PasswordPolicyMinimumCrackTimeSeconds,
    PasswordPolicyRejectionReason,
    type PasswordPolicyEvaluation,
} from '@pillar-portal/api-types/dist/password-policy.types';
import { authApi } from '@/lib/pillarApi';
import { cn } from '@/lib/utils';

type BackendPasswordPolicyStatus = 'idle' | 'checking' | 'accepted' | 'rejected' | 'error';

interface BackendPasswordPolicyState {
    readonly status: BackendPasswordPolicyStatus;
    readonly rejectedReasons: readonly CheckProposedPasswordRejectedReason[];
}

export interface PasswordPolicyFeedbackState {
    readonly accepted: boolean;
    readonly localEvaluation: PasswordPolicyEvaluation;
    readonly backendState: BackendPasswordPolicyState;
}

export function passwordPolicyBlocksSubmit(
    state: PasswordPolicyFeedbackState,
    password: string
): boolean {
    return password.length === 0 || !state.accepted;
}

function describePolicyRejection(reason: CheckProposedPasswordRejectedReason): string {
    switch (reason) {
        case PasswordPolicyRejectionReason.PasswordTooLong:
            return 'Password must be no more than 1023 characters.';
        case PasswordPolicyRejectionReason.DisallowedDefaultPassword:
            return 'This default password is not allowed.';
        case PasswordPolicyRejectionReason.ZxcvbnTooWeak:
            return 'Password must take at least 100 years to crack.';
        case 'COMPROMISED_PASSWORD_CORPUS_MATCH':
            return 'This password appears in the compromised-password corpus.';
        case 'COMPROMISED_PASSWORD_CORPUS_UNAVAILABLE':
            return 'The compromised-password corpus is unavailable.';
        default:
            return 'Password does not satisfy the password policy.';
    }
}

function firstRejectionMessage(
    localEvaluation: PasswordPolicyEvaluation,
    backendState: BackendPasswordPolicyState
): string | null {
    const localReason = localEvaluation.rejected_reasons[0];
    if (localReason !== undefined) {
        return describePolicyRejection(localReason);
    }

    const backendReason = backendState.rejectedReasons[0];
    if (backendReason !== undefined) {
        return describePolicyRejection(backendReason);
    }

    if (backendState.status === 'error') {
        return 'Password policy check failed. Try again before submitting.';
    }

    return null;
}

export function usePasswordPolicyFeedback(password: string): PasswordPolicyFeedbackState {
    const localEvaluation: PasswordPolicyEvaluation = useMemo(
        () => evaluatePasswordPolicy({ password }),
        [password]
    );
    const [backendState, setBackendState] = useState<BackendPasswordPolicyState>({
        status: 'idle',
        rejectedReasons: [],
    });

    useEffect(() => {
        if (password.length === 0 || !localEvaluation.accepted) {
            setBackendState({ status: 'idle', rejectedReasons: [] });
            return;
        }

        let isCurrent = true;
        setBackendState({ status: 'checking', rejectedReasons: [] });

        authApi
            .checkProposedPassword({ password })
            .then((response: CheckProposedPasswordResponse) => {
                if (!isCurrent) {
                    return;
                }
                setBackendState({
                    status: response.accepted ? 'accepted' : 'rejected',
                    rejectedReasons: response.rejected_reasons,
                });
            })
            .catch(() => {
                if (!isCurrent) {
                    return;
                }
                setBackendState({ status: 'error', rejectedReasons: [] });
            });

        return () => {
            isCurrent = false;
        };
    }, [localEvaluation.accepted, password]);

    return {
        accepted: localEvaluation.accepted && backendState.status === 'accepted',
        localEvaluation,
        backendState,
    };
}

export function PasswordPolicyFeedback(props: {
    readonly password: string;
    readonly state: PasswordPolicyFeedbackState;
}): ReactElement | null {
    if (props.password.length === 0) {
        return null;
    }

    const estimate = props.state.localEvaluation.zxcvbn_estimate;
    const rejectionMessage = firstRejectionMessage(
        props.state.localEvaluation,
        props.state.backendState
    );
    const estimateMeetsPolicy =
        estimate !== null &&
        estimate.offline_slow_hashing_1e4_per_second_seconds >=
            PasswordPolicyMinimumCrackTimeSeconds;

    return (
        <div className="space-y-2">
            {estimate !== null && (
                <p
                    className={cn(
                        'text-xs font-medium',
                        estimateMeetsPolicy ? 'text-green-700' : 'text-red-700'
                    )}
                >
                    Estimated time to crack: {estimate.offline_slow_hashing_1e4_per_second_display}
                </p>
            )}

            {props.state.backendState.status === 'checking' && (
                <p className="text-xs text-muted-foreground">Checking password corpus...</p>
            )}

            {rejectionMessage !== null && (
                <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                    {rejectionMessage}
                </div>
            )}
        </div>
    );
}
